import { useState, useEffect } from 'react';
import axios from 'axios';
import { Workflow, Plus } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function WorkflowBuilder() {
  const [workflows, setWorkflows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_event: '',
    conditions: [],
    actions: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/workflows`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerEvents = [
    'call:answered',
    'call:ended',
    'call:disposition:set',
    'contact:created',
    'agent:status:changed'
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <Workflow className="w-6 h-6" />
          Workflow Automation
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Workflow
        </button>
      </div>

      {showForm && (
        <div className="border rounded p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Workflow Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g., Auto-send follow-up email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Trigger Event</label>
            <select
              value={formData.trigger_event}
              onChange={(e) => setFormData({ ...formData, trigger_event: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select trigger...</option>
              {triggerEvents.map((event) => (
                <option key={event} value={event}>
                  {event}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                // In a full implementation, this would open a workflow builder UI
                alert('Workflow builder UI coming soon! Use the API to create workflows for now.');
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Build Workflow
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setFormData({ name: '', description: '', trigger_event: '', conditions: [], actions: [] });
              }}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Loading workflows...</div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No workflows configured</div>
      ) : (
        <div className="space-y-2">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="border rounded p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{workflow.name}</p>
                  {workflow.description && (
                    <p className="text-sm text-gray-600">{workflow.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Trigger: {workflow.trigger_event}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  workflow.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {workflow.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

